import { type JSX, useMemo } from "react";

import { useHistory } from "react-router-dom";
import { Button, Stack, Tooltip } from "@mui/material";
import { type AlertColor } from "@mui/material/Alert";

import { errorsText } from "../../../assets/texts/CloudAnalytics";
import { useCustomerId } from "../../../Components/hooks/useCustomerId";
import { navigatorSubscriptionURL } from "../../../utils/common";
import { ErrorCode } from "../utilities";
import { CenteredMessage } from "./CenteredMessage";

type Props = {
  disableCacheBtn: boolean;
  errorCode: string;
  errorMessage?: string;
  onCacheReportClicked: () => void;
};

export function ErrorMessages({ onCacheReportClicked, disableCacheBtn, errorCode, errorMessage }: Props) {
  const history = useHistory();
  const customerId = useCustomerId();

  const tooltipTitle = disableCacheBtn ? errorsText.NOT_SAVED_TOOLTIP : "";

  const showCacheReportButton = errorCode === ErrorCode.QUERY_TIMEOUT_524 || errorCode === ErrorCode.FAILED_TO_CACHE;

  const handleExpertInquiry = () => {
    history.push(`/customers/${customerId}/support?open-case-iq=true/`);
  };

  const { description, title, list, showExpertInquiry, severity } = useMemo(() => {
    let description: string | JSX.Element = "";
    let title = "";
    const list: string[] = [];
    let showExpertInquiry = false;
    let severity: AlertColor = "error";

    switch (errorCode) {
      case ErrorCode.ENTITLEMENT_NOT_ENABLED:
        severity = "info";
        if (errorMessage) {
          description = errorMessage;
        } else {
          description = errorsText.ENTITLEMENT_NOT_ENABLED.DESCRIPTION;
          showExpertInquiry = true;
        }
        break;
      case ErrorCode.OUTSIDE_ORGANIZATION:
        description = errorsText.OUTSIDE_ORGANIZATION.DESCRIPTION;
        showExpertInquiry = true;
        break;
      case ErrorCode.EMPTY:
        severity = "info";
        description = errorsText.EMPTY.DESCRIPTION;
        break;
      case ErrorCode.SERIES_TOO_LARGE:
        description = errorsText.SERIES_TOO_LARGE.DESCRIPTION;
        break;
      case ErrorCode.TOO_LARGE:
        description = errorsText.TOO_LARGE.DESCRIPTION;
        break;
      case ErrorCode.QUERY_TIMEOUT:
        description = errorsText.QUERY_TIMEOUT.DESCRIPTION;
        break;
      case ErrorCode.EMPTY_DUE_TO_TREND:
        severity = "warning";
        description = errorsText.EMPTY_DUE_TO_TREND.DESCRIPTION;
        break;
      case ErrorCode.QUERY_TIMEOUT_524:
        severity = "info";
        title = errorsText.QUERY_TIMEOUT_524.TITLE;
        description = errorsText.QUERY_TIMEOUT_524.DESCRIPTION;
        break;
      case ErrorCode.EXCEEDED_FIRESTORE_LIMIT:
        description = errorsText.EXCEEDED_FIRESTORE_LIMIT.DESCRIPTION;
        showExpertInquiry = true;
        break;
      case ErrorCode.CALCULATED_METRIC_NOT_PROVIDED:
        description = errorsText.NO_ACCESS_METRIC;
        showExpertInquiry = true;
        break;
      case ErrorCode.EXTENDED_METRIC_NOT_PROVIDED:
        description = errorsText.NO_ACCESS_METRIC_EXTENDED;
        showExpertInquiry = true;
        break;
      case ErrorCode.BQ_PROXY_NOT_FOUND:
        description = errorsText.BQ_PROXY_NOT_FOUND.DESCRIPTION;
        showExpertInquiry = true;
        break;
      case ErrorCode.BQ_PROXY_FORBIDDEN:
        description = errorsText.BQ_PROXY_FORBIDDEN.DESCRIPTION;
        showExpertInquiry = true;
        break;
      case ErrorCode.BQ_PROXY_IAM_PERMISSION_DENIED:
        description = errorsText.BQ_PROXY_IAM_PERMISSION_DENIED.DESCRIPTION;
        showExpertInquiry = true;
        break;
      case ErrorCode.FAILED_TO_CACHE:
        severity = "warning";
        title = errorsText.FAILED_TO_CACHE.TITLE;
        description = errorsText.FAILED_TO_CACHE.DESCRIPTION;
        break;
      default:
        description = errorsText.DEFAULT.DESCRIPTION;
        showExpertInquiry = true;
        break;
    }

    return { description, title, list, showExpertInquiry, severity };
  }, [errorCode, errorMessage]);

  const cacheReportButton = showCacheReportButton ? (
    <Tooltip title={tooltipTitle} disableHoverListener={!disableCacheBtn}>
      <span>
        <Button
          variant="contained"
          disabled={disableCacheBtn}
          onClick={onCacheReportClicked}
          data-cy="error-cache-report-button"
        >
          {errorsText.CACHE_REPORT_BTN}
        </Button>
      </span>
    </Tooltip>
  ) : null;

  const expertInquiryButton = showExpertInquiry ? (
    <Button onClick={handleExpertInquiry} variant="contained" data-cy="error-expert-inquiry-button">
      {errorsText.EXPERT_INQUIRY}
    </Button>
  ) : null;

  const entitlementButton = errorCode === ErrorCode.ENTITLEMENT_NOT_ENABLED && (
    <Button
      href={navigatorSubscriptionURL}
      target="_blank"
      rel="noreferrer"
      variant="outlined"
      data-cy="error-learn-more-button"
    >
      {errorsText.LEARN_MORE}
    </Button>
  );

  const actionButtons = (
    <Stack direction="row" spacing={2} justifyContent="center">
      {cacheReportButton}
      {expertInquiryButton}
      {entitlementButton}
    </Stack>
  );

  return (
    <CenteredMessage severity={severity} title={title} description={description}>
      <Stack spacing={2} alignItems="center">
        {list.length > 0 && (
          <ul>
            {list.map((l) => (
              <li key={l}>{l}</li>
            ))}
          </ul>
        )}
        {actionButtons}
      </Stack>
    </CenteredMessage>
  );
}
